import {
  Stack,
  Typography,
  Switch,
} from '@mui/material';
import { ProductGroups } from 'enums';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import CustomPlaceholders from './custom-placeholders';
import { Offer, CustomPlaceholder } from '../../campaign-builder.types';
import { coerceOfferFieldToString } from '../../offer-field.utils';

interface Props {
  productGroup: ProductGroups;
  offer: Offer;
  customPlaceholders: CustomPlaceholder[];
  handleAddCustomPlaceholder(title: string, text: string): void;
  handleDeleteCustomPlaceholder(key: string): void;
  handleEditCustomPlaceholder(originalKey: string, newKey: string, newValue: string): void;
  handleOfferInputChange(type: keyof Offer): (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOfferToggleChange(type: keyof Offer): (inUse: boolean) => void;
  fieldInUse: { [key: string]: boolean };
}

const UpdateAiBriefing: React.FC<Props> = ({
  productGroup,
  offer,
  customPlaceholders,
  handleAddCustomPlaceholder,
  handleDeleteCustomPlaceholder,
  handleEditCustomPlaceholder,
  handleOfferInputChange,
  handleOfferToggleChange,
  fieldInUse,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  /* ----------------- custom placeholder state ---------------- */
  const [customPlaceholdersExpanded, setCustomPlaceholdersExpanded] = useState<boolean>(customPlaceholders.length > 0);

  const handleCustomPlaceholdersAccordionChange = (_: React.SyntheticEvent, expanded: boolean) => {
    setCustomPlaceholdersExpanded(expanded);
  };

  // Helper function to handle product-specific translations
  const getLocalizedText = (key: string): string => {
    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      const leadBoosterKey = `${key}_LeadBooster`;
      return t(leadBoosterKey) || t(key);
    }
    // Default is for Active Sourcing
    const activeSourcingKey = `${key}_ActiveSourcing`;
    return t(activeSourcingKey) || t(key);
  };

  enum Placeholders {
    JOB_OFFER = '[[{{jobOffer}}]]',
    PROFILE = '[[{{profile}}]]',
    MESSAGE_CONTEXT = '[[{{messageContext}}]]',
    PROBLEMS = '[[{{problems}}]]',
    LANG = '{{lang}}',
    FEATURES = '{{features}}',
    BENEFITS = '{{benefits}}',
    IMPACT = '{{impact}}',
  }

  // Determine if toggles should be shown
  const showFieldToggles = productGroup === ProductGroups.LEAD_BOOSTER ? customPlaceholdersExpanded : true;

  return (
    <Stack className={classes.updateAiBriefing} gap={3}>
      <Stack gap={2}>
        <Stack gap={1}>
          <Typography className={classes.subTitle}>{getLocalizedText('updateAiBriefingTitle')}</Typography>
          <Typography className={classes.text}>{getLocalizedText('updateAiBriefingText')}</Typography>
        </Stack>
        <Stack gap={2}>
          {productGroup === ProductGroups.HR_BOOSTER ? (
            <>
              <Stack gap={0.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography className={classes.subTitle}>{t('jobOffer')}</Typography>
                    <Typography className={classes.greyText}>{Placeholders.JOB_OFFER}</Typography>
                  </Stack>
                  {showFieldToggles && (
                    <Switch size="small" checked={fieldInUse.jobOffer} onChange={(_, c) => handleOfferToggleChange('jobOffer')(c)} />
                  )}
                </Stack>
                {fieldInUse.jobOffer && (
                  <PaiTextArea
                    value={coerceOfferFieldToString(offer.jobOffer)}
                    rows={5}
                    placeholder={t('jobOfferPlaceholder')}
                    onChange={handleOfferInputChange('jobOffer')}
                  />
                )}
              </Stack>
            </>
          ) : (
            <>
              <Stack gap={0.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography className={classes.subTitle}>{t('problems')}</Typography>
                    <Typography className={classes.greyText}>{Placeholders.PROBLEMS}</Typography>
                  </Stack>
                  {showFieldToggles && (
                    <Switch size="small" checked={fieldInUse.problems} onChange={(_, c) => handleOfferToggleChange('problems')(c)} />
                  )}
                </Stack>
                {fieldInUse.problems && (
                  <PaiTextArea
                    value={coerceOfferFieldToString(offer.problems)}
                    rows={5}
                    placeholder={getLocalizedText('productProblemsPlaceholder')}
                    onChange={handleOfferInputChange('problems')}
                  />
                )}
              </Stack>
              <Stack gap={0.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography className={classes.subTitle}>{t('features')}</Typography>
                    <Typography className={classes.greyText}>{Placeholders.FEATURES}</Typography>
                  </Stack>
                  {showFieldToggles && (
                    <Switch size="small" checked={fieldInUse.features} onChange={(_, c) => handleOfferToggleChange('features')(c)} />
                  )}
                </Stack>
                {fieldInUse.features && (
                  <PaiTextArea
                    value={coerceOfferFieldToString(offer.features)}
                    rows={5}
                    placeholder={getLocalizedText('productFeaturesPlaceholder')}
                    onChange={handleOfferInputChange('features')}
                  />
                )}
              </Stack>
              <Stack gap={0.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography className={classes.subTitle}>{t('benefits')}</Typography>
                    <Typography className={classes.greyText}>{Placeholders.BENEFITS}</Typography>
                  </Stack>
                  {showFieldToggles && (
                    <Switch size="small" checked={fieldInUse.benefits} onChange={(_, c) => handleOfferToggleChange('benefits')(c)} />
                  )}
                </Stack>
                {fieldInUse.benefits && (
                  <PaiTextArea
                    value={coerceOfferFieldToString(offer.benefits)}
                    rows={5}
                    placeholder={getLocalizedText('productBenefitsPlaceholder')}
                    onChange={handleOfferInputChange('benefits')}
                  />
                )}
              </Stack>
              <Stack gap={0.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography className={classes.subTitle}>{t('impact')}</Typography>
                    <Typography className={classes.greyText}>{Placeholders.IMPACT}</Typography>
                  </Stack>
                  {showFieldToggles && (
                    <Switch size="small" checked={fieldInUse.impact} onChange={(_, c) => handleOfferToggleChange('impact')(c)} />
                  )}
                </Stack>
                {fieldInUse.impact && (
                  <PaiTextArea
                    value={coerceOfferFieldToString(offer.impact)}
                    rows={5}
                    placeholder={getLocalizedText('productImpactPlaceholder')}
                    onChange={handleOfferInputChange('impact')}
                  />
                )}
              </Stack>
            </>
          )}
        </Stack>
        <Typography className={classes.greyText}>{getLocalizedText('updateFieldNote')}</Typography>

        <CustomPlaceholders
          customPlaceholders={customPlaceholders}
          expanded={customPlaceholdersExpanded}
          onExpandedChange={handleCustomPlaceholdersAccordionChange}
          handleAddCustomPlaceholder={handleAddCustomPlaceholder}
          handleDeleteCustomPlaceholder={handleDeleteCustomPlaceholder}
          handleEditCustomPlaceholder={handleEditCustomPlaceholder}
        />
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  updateAiBriefing: {
    paddingBottom: '32px',
    marginBottom: '16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
  },
  text: {
    fontSize: theme.typography.pxToRem(14),
  },
  greyText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  subtleAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&::before': { display: 'none' },
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: 4,
  },
  subtleAccordionSummary: {
    minHeight: 40,
    '& .MuiAccordionSummary-content': { marginTop: 0, marginBottom: 0 },
  },
  subtleAccordionDetails: {
    paddingTop: 0,
    backgroundColor: 'transparent',
  },
  placeholderItem: {
    position: 'relative',
    backgroundColor: theme.palette.greys.lightest,
    padding: 8,
    borderRadius: 4,
    border: `1px solid ${theme.palette.greys.lighter}`,
    boxShadow: 'none',
  },
}));

export default UpdateAiBriefing;
